import { collection, getDocs } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import StateTransition from '@src/models/StateTransition';
import safeExecute from '@src/util/safeExecute';

class ExecutionStateTransitionsRepository {

  /**
   * Get the path to the state transitions subcollection
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {string} Path to state transitions subcollection
   */
  getStateTransitionsPath(organizationId, divisionId, executionId) {
    return `${FSCollections.ORGANIZATIONS}/${organizationId}/${FSCollections.DIVISIONS}/${divisionId}/${FSCollections.STATE_TRANSITIONS}/${executionId}/transitions`;
  }

  /**
   * Get execution state transitions from Firestore
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<ExecutionStateTransitions|null>} ExecutionStateTransitions instance or null if not found
   */
  async get(organizationId, divisionId, executionId) {
    return safeExecute(async () => {
      if (!organizationId || !divisionId || !executionId) {
        return null;
      }

      const stateTransitionsRef = collection(
        db,
        this.getStateTransitionsPath(organizationId, divisionId, executionId)
      );
      const stateTransitions = await getDocs(stateTransitionsRef);
      const stateTransitionsData = stateTransitions.docs.map(doc =>
        StateTransition.fromFirestore(doc.data(), doc.id),
      );
      return stateTransitionsData;
    });
  }

  /**
   * Map Cloud Function error codes to user-friendly messages
   * @param {string} errorCode - Firebase error code
   * @returns {string} User-friendly error message
   */
  getErrorMessage(errorCode) {
    const errorMessages = {
      unauthenticated: 'User not authenticated',
      'invalid-argument': 'Missing or invalid parameters',
      'not-found': 'Execution not found',
      'failed-precondition': 'Invalid state transition',
      internal: 'Internal server error',
    };

    return errorMessages[errorCode] || 'An unexpected error occurred';
  }
}

export default ExecutionStateTransitionsRepository;
